import { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMutation } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { createLogger } from '../../utils/logger-ssr-safe';

const logger = createLogger({ module: 'ProfileSyncHandler' });

/**
 * Component that handles syncing user profile data from WorkOS to Convex
 * This runs after authentication is established as a fallback to the callback page sync
 */
export function ProfileSyncHandler() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const syncUser = useMutation(api.users.sync);
  const syncAttemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // SSR safety check - only run in browser
    if (typeof window === 'undefined') {
      return;
    }

    // Wait for auth to finish loading
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      return;
    }

    // Check if we've already attempted sync for this user in this session
    if (syncAttemptedRef.current.has(user.id)) {
      return;
    }

    // Check if already synced in this browser session
    if (sessionStorage.getItem(`synced_to_convex_${user.id}`)) {
      syncAttemptedRef.current.add(user.id);
      return;
    }

    const syncProfile = async () => {
      try {
        // Mark that we've attempted sync for this user
        syncAttemptedRef.current.add(user.id);

        logger.info('Syncing user profile to Convex...', { userId: user.id });

        // Sync user with Convex - this creates/updates the user record
        await syncUser({ workosUserId: user.id });

        logger.info('Profile synced to Convex successfully');
        sessionStorage.setItem(`synced_to_convex_${user.id}`, 'true');

        // Clean up any pending profile data from old flow
        sessionStorage.removeItem('pending_profile');
      } catch (error) {
        logger.error('Profile sync error:', error);
        // Remove from attempted set so it can retry later
        syncAttemptedRef.current.delete(user.id);
      }
    };

    syncProfile();
  }, [user, isAuthenticated, isLoading, syncUser]);

  // This component doesn't render anything
  return null;
}